import { Card, CardContent } from '@/common/ui/card'
import { Skeleton } from '@/common/ui/skeleton'
import {
	Users,
	TrendingUp,
	TrendingDown,
	Wallet,
	Coins,
	MinusCircle,
	PlusCircle,
	Gift,
} from 'lucide-react'
import { PartnerPlayersSummaryTotals } from '@/modules/manage-player/type'

interface PartnerPlayersSummaryCardsProps {
	summary?: PartnerPlayersSummaryTotals
	totalPlayers?: number
	isLoading?: boolean
}

const formatThaiCurrency = (value: number) => {
	return value.toLocaleString('th-TH', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})
}

export default function PartnerPlayersSummaryCards({
	summary,
	totalPlayers = 0,
	isLoading = false,
}: PartnerPlayersSummaryCardsProps) {
	if (isLoading) {
		return (
			<div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-4">
				{Array.from({ length: 8 }).map((_, i) => (
					<Card key={i}>
						<CardContent className="p-3 sm:p-4">
							<div className="flex items-center gap-2">
								<Skeleton className="h-8 w-8 rounded-full" />
								<div className="flex-1 space-y-1">
									<Skeleton className="h-3 w-16" />
									<Skeleton className="h-5 w-24" />
								</div>
							</div>
						</CardContent>
					</Card>
				))}
			</div>
		)
	}

	const profit = summary?.summaryProfit ?? 0
	const isProfitPositive = profit >= 0
	const partnerShare = summary?.summaryPartnerShare ?? 0

	const summaryItems = [
		{
			label: 'จำนวนผู้เล่น',
			value: totalPlayers.toLocaleString('th-TH'),
			icon: Users,
			iconColor: 'text-blue-500',
			bgColor: 'bg-blue-500/10',
		},
		{
			label: 'ยอดฝากรวม',
			value: formatThaiCurrency(summary?.summaryTotalDeposit ?? 0),
			icon: TrendingUp,
			iconColor: 'text-green-500',
			bgColor: 'bg-green-500/10',
		},
		{
			label: 'ยอดถอนรวม',
			value: formatThaiCurrency(summary?.summaryTotalWithdraw ?? 0),
			icon: TrendingDown,
			iconColor: 'text-red-500',
			bgColor: 'bg-red-500/10',
		},
		{
			label: 'แทงเสีย',
			value: formatThaiCurrency(summary?.summaryTransferIn ?? 0),
			icon: MinusCircle,
			iconColor: 'text-red-600',
			bgColor: 'bg-red-600/10',
		},
		{
			label: 'แทงได้',
			value: formatThaiCurrency(summary?.summaryTransferOut ?? 0),
			icon: PlusCircle,
			iconColor: 'text-green-600',
			bgColor: 'bg-green-600/10',
		},
		{
			label: 'โบนัส',
			value: formatThaiCurrency(summary?.summaryBonus ?? 0),
			icon: Gift,
			iconColor: 'text-amber-600',
			bgColor: 'bg-amber-500/10',
		},
		{
			label: 'กำไร/ขาดทุน',
			value: `${isProfitPositive ? '' : '-'}${formatThaiCurrency(Math.abs(profit))}`,
			icon: Wallet,
			iconColor: isProfitPositive ? 'text-emerald-500' : 'text-red-500',
			bgColor: isProfitPositive ? 'bg-emerald-500/10' : 'bg-red-500/10',
		},
		{
			label: 'ส่วนแบ่งรวม',
			value: formatThaiCurrency(partnerShare),
			icon: Coins,
			iconColor: 'text-purple-500',
			bgColor: 'bg-purple-500/10',
		},
	]

	return (
		<div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-4">
			{summaryItems.map((item) => (
				<Card key={item.label}>
					<CardContent className="p-3 sm:p-4">
						<div className="flex items-center gap-2 sm:gap-3">
							<div className={`rounded-full p-2 ${item.bgColor}`}>
								<item.icon
									className={`h-4 w-4 sm:h-5 sm:w-5 ${item.iconColor}`}
								/>
							</div>
							<div className="min-w-0 flex-1">
								<p className="truncate text-xs text-muted-foreground sm:text-sm">
									{item.label}
								</p>
								<p className="truncate text-sm font-semibold sm:text-base lg:text-lg">
									{item.value}
								</p>
							</div>
						</div>
					</CardContent>
				</Card>
			))}
		</div>
	)
}
